
// A purchase invoice while DLInsightsBotServer reads its document, or after a reading that failed.
// Shows the progress, follows the reading on the server and hands the invoice back to the
// list when the reading arrives (the detail view then shows the detected lines); after a
// failure shows the reason and offers to read the document again.

class PurchaseInvoiceProcessingViewController extends MUIViewController
{
    static newInstance(): PurchaseInvoiceProcessingViewController {
        let vc = new PurchaseInvoiceProcessingViewController("purchase-invoice-processing-view");
        vc.initWithResource("layout/invoices/PurchaseInvoiceProcessingView.html");
        return vc;
    }

    private documentIDLabel:MUILabel = null;
    private spinnerView:MUIView = null;
    private statusLabel:MUILabel = null;
    private reasonLabel:MUILabel = null;
    private viewFilesButton:MUIButton = null;
    private rescanButton:MUIButton = null;
    private cancelDeleteButton:MUIButton = null;

    viewDidLoad(){
        super.viewDidLoad();

        this.documentIDLabel = MUIOutlet(this, "document-id-lbl", "MUILabel");
        this.spinnerView = MUIOutlet(this, "spinner-view", "MUIView");
        this.statusLabel = MUIOutlet(this, "status-lbl", "MUILabel");
        this.reasonLabel = MUIOutlet(this, "reason-lbl", "MUILabel");

        this.viewFilesButton = MUIOutlet(this, "view-files-btn", "MUIButton");
        this.viewFilesButton.setAction(this, function(this:PurchaseInvoiceProcessingViewController){
            this.openFilesViewController();
        });

        this.rescanButton = MUIOutlet(this, "rescan-btn", "MUIButton");
        this.rescanButton.setAction(this, function(this:PurchaseInvoiceProcessingViewController){
            this.rescan();
        });

        this.cancelDeleteButton = MUIOutlet(this, "cancel-delete-btn", "MUIButton");
        this.cancelDeleteButton.setAction(this, function(this:PurchaseInvoiceProcessingViewController){
            this.confirmCancelAndDelete();
        });
    }

    viewWillAppear(animate?){
        super.viewWillAppear(animate);
        this.updateUI();
        this.watch();
    }

    viewWillDisappear(animate?){
        super.viewWillDisappear(animate);
        this.watching = false;
    }

    private _invoice:PurchaseInvoice = null;
    /// The reading state shown: the invoice's, or scanning right after RE-SCAN.
    private state:number = DBDocumentOCRStatus.scanning;
    private reason:string = null;
    /// What the server said about the reading: whether it is reading the document right now
    /// (for how many seconds), and whether it has an engine at all. Null until it answered.
    private readingSeconds:number = null;
    private engineMissing:boolean = false;
    private serverAnswered:boolean = false;

    set invoice(value:PurchaseInvoice){
        this._invoice = value;
        this.state = value != null ? value.ocrStatus : DBDocumentOCRStatus.scanning;
        this.reason = value != null ? value.ocrError : null;
        this.readingSeconds = null;
        this.engineMissing = false;
        this.serverAnswered = false;
        this.updateUI();
        if (this.viewIsLoaded) this.watch();
    }

    private updateUI(){
        if (!this.viewIsLoaded || this._invoice == null) return;
        this.documentIDLabel.text = this._invoice.documentNumber ?? null;

        let failed = this.state == DBDocumentOCRStatus.failed;
        // nothing is reading the document, as far as the server knows: the user can start it
        let idle = !failed && this.serverAnswered && this.readingSeconds == null;
        let statusLayer = this.statusLabel.layer as HTMLElement;
        let reasonLayer = this.reasonLabel.layer as HTMLElement;
        statusLayer.classList.remove("failed");
        reasonLayer.classList.remove("notice");
        this.spinnerView.hidden = failed || idle;

        if (failed) {
            this.statusLabel.text = MIOLocalizeString("THE_READING_FAILED", "THE READING FAILED");
            statusLayer.classList.add("failed");
            this.reasonLabel.hidden = false;
            this.reasonLabel.text = DocumentExtractionLineCell.escape(this.reason ?? MIOLocalizeString("THE_DOCUMENT_COULD_NOT_BE_READ", "The document could not be read."));
        }
        else if (!this.serverAnswered) {
            this.statusLabel.text = MIOLocalizeString("CHECKING_THE_READING", "Checking the reading…");
            this.reasonLabel.hidden = true;
        }
        else if (idle) {
            this.statusLabel.text = MIOLocalizeString("NOTHING_IS_READING_THIS_DOCUMENT", "Nothing is reading this document right now.");
            this.reasonLabel.hidden = false;
            reasonLayer.classList.add("notice");
            this.reasonLabel.text = this.engineMissing ? MIOLocalizeString("THE_READING_SERVER_HAS_NO_ENGINE", "The reading server has no engine configured (ANTHROPIC_API_KEY).")
                                                       : MIOLocalizeString("THE_READING_DID_NOT_START_RE_SCAN", "The reading did not start: the reading server was not woken up, or it restarted. Start it with RE-SCAN.");
        }
        else {
            let seconds = this.readingSeconds;
            this.statusLabel.text = MIOLocalizeString("THE_DOCUMENT_IS_BEING_READ_NOW", "The document is being read right now. The invoice opens by itself when the reading arrives.")
                                  + (seconds > 0 ? " (" + seconds + " s)" : "");
            this.reasonLabel.hidden = true;
        }
        this.rescanButton.hidden = !(failed || idle);
    }

    // MARK: following the reading on the server

    private watching = false;

    private watch(){
        if (this.watching || this._invoice == null || this.state != DBDocumentOCRStatus.scanning) return;
        this.watching = true;
        this.poll(this._invoice);
    }

    private poll(invoice:PurchaseInvoice){
        if (!this.watching || this._invoice !== invoice) return;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        setTimeout(() => {
            if (!this.watching || this._invoice !== invoice) return;
            ad.webService.documentExtractionStatus("PurchaseInvoice", invoice.identifier, (code:number, data:any) => {
                if (!this.watching || this._invoice !== invoice) return;
                let status = code == 200 && data != null && data["ocrStatus"] != null ? Number(data["ocrStatus"]) : null;
                if (status == DBDocumentOCRStatus.scanning) {
                    // still being read, or waiting for someone to read it: say which
                    this.serverAnswered = true;
                    this.readingSeconds = data["readingSince"] != null ? Number(data["readingSeconds"] ?? 0) : null;
                    this.engineMissing = data["engine"] != null && String(data["engine"]).length == 0;
                    this.updateUI();
                    this.poll(invoice);
                    return;
                }
                if (status == null) { this.poll(invoice); return; }
                this.watching = false;
                if (status == DBDocumentOCRStatus.failed) {
                    this.state = status;
                    this.reason = data["ocrError"] ?? null;
                    this.updateUI();
                }
                // the server moved on: the list takes the fresh invoice to the view that fits
                DocumentExtractionSession.showInvoiceAfterRefresh(invoice.identifier);
            });
        }, 3000);
    }

    // Reads the document again: a new engine run on the same files.
    private rescan(){
        if (this._invoice == null) return;
        let invoice = this._invoice;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        this.rescanButton.enabled = false;
        ad.webService.runDocumentExtraction("PurchaseInvoice", invoice.identifier, (code:number, data:any) => {
            this.rescanButton.enabled = true;
            if (this._invoice !== invoice) return;
            if (code != 200) {
                AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("COULDNT_START_THE_EXTRACTION", "Couldn't start the extraction."));
                return;
            }
            this.state = DBDocumentOCRStatus.scanning;
            this.reason = null;
            this.serverAnswered = true;
            this.readingSeconds = 0;     // started: the next poll says how it goes
            this.updateUI();
            this.watch();
        });
    }

    private openFilesViewController(){
        if (this._invoice == null) return;
        let vc = InvoiceFilesViewController.newInstance();
        vc.document = this._invoice;
        this.presentViewController(vc, true);
    }

    private confirmCancelAndDelete(){
        if (this._invoice == null) return;

        let avc = new MUIAlertViewController();
        avc.initWithTitle(
            MIOLocalizeString("CONFIRM", "CONFIRM"),
            MIOLocalizeString("DELETE_INVOICE_CONFIRM", "Are you sure you want to cancel and delete this invoice?"),
            MUIAlertViewStyle.Default
        );

        avc.addAction(MUIAlertAction.alertActionWithTitle(
            MIOLocalizeString("DELETE", "DELETE"),
            MUIAlertActionStyle.Destructive,
            this,
            function(this:PurchaseInvoiceProcessingViewController){                
                this.watching = false;
                DBHelper.deleteObjectFromMainContext(this._invoice, true);
                this._invoice = null;
                MIONotificationCenter.defaultCenter().postNotification(this, "ReloadAndShowPurchaseInvoiceNotification");
            }
        ));

        avc.addAction(MUIAlertAction.alertActionWithTitle(
            MIOLocalizeString("CANCEL", "CANCEL"),
            MUIAlertActionStyle.Cancel,
            null,
            null
        ));

        this.presentViewController(avc, true);
    }
}
